import { AccountRoutes } from '@account/routes';
import { countryRestrictions } from '@authorizedInstaller/constants';
import { BlogViews } from '@blog/routes/public';
import { Roles } from '@common/types/user';
import { CompaniesList } from '@company/routes/dashboard/CompaniesList';
import { CompanyOverview } from '@company/routes/public/CompanyOverview';
import { CompanySetup } from '@company/routes/public/Setup/CompanySetup';
import { MediaBrowserToolbar } from '@components/display/toolbars';
import { ProtectedRoutes, SplitLayout } from '@components/layout';
import { PublicLayout, PublicLayoutContent, PublicLayoutOptions } from '@components/layout/public';
import { DevelopersRoutes } from '@developers/routes/DevelopersRoutes';
import { EventDetail } from '@event/routes/public';
import { ExtendedWarrantyContextProvider } from '@extendedWarranty/contexts/ExtendedWarrantyContext';
import { EWUploadFlowWrapper } from '@extendedWarranty/routes/dashboard/EWUploadFlowWrapper';
import { InstallationOverview } from '@extendedWarranty/routes/dashboard/InstallationOverview';
import { Firmwares } from '@firmwares/routes';
import { useAuth } from '@hooks/useAuth';
import { MediaAssets } from '@mediaAssets/routes';
import { NewsRoutes } from '@news/routes';
import { OnlineTrainingViews } from '@onlineTraining/routes/public';
import { Overview } from '@overview/routes';
import { RepairDocs } from '@repairdocs/routes';
import { RLAForm } from '@rla/routes';
import { Forbidden } from '@routes/Forbidden';
import { AuthViews } from '@routes/public/Authentication';
import { useGetIsFlagEnabledQuery } from '@services/featureFlag';
import { SoftwareIntegrator } from '@softwareIntegrator/routes/public';
import { VideosRoutes } from '@videos/routes/VideosRoutes';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';
import { AuthorizedInstallerApplicationForm } from '../../features/authorizedInstaller/routes/public/ApplicationForm/AppplicationForm';
import { EventList } from '../../features/event/routes/public/EventList';

const MEDIA_ASSETS_TOOLBAR = <MediaBrowserToolbar />;
const EVENT_LIST_DOTTED_BACKGROUND = { gradient: true, gradientValue: '10%' } as const;

const PublicViews = () => {
    const { pathname } = useLocation();
    const { isLoggedIn, user } = useAuth();
    const { data: isExtWarrEnabled } = useGetIsFlagEnabledQuery('EXTENDED_WARRANTY', {
        skip: !isLoggedIn,
    });
    const { data: isAuthInstEnabled } = useGetIsFlagEnabledQuery('AUTHORIZED_INSTALLER_PROGRAM', {
        skip: !isLoggedIn,
    });
    const { data: isCompanyEntityEnabled } = useGetIsFlagEnabledQuery('COMPANY_ENTITY', {
        skip: !isLoggedIn,
    });

    if (pathname === '/app/auth' || pathname.startsWith('/app/auth/')) {
        return <AuthViews />;
    }

    return (
        <PublicLayout>
            <Routes>
                <Route element={<ProtectedRoutes />}>
                    <Route index element={<Overview />} />
                    {isExtWarrEnabled?.is_enabled ? (
                        <Route path="extended-warranty" element={<Outlet />}>
                            <Route index element={<Navigate to="overview" replace />} />
                            <Route path="overview" element={<InstallationOverview />} />
                            <Route
                                path="upload-serial-numbers"
                                element={
                                    <ExtendedWarrantyContextProvider>
                                        <EWUploadFlowWrapper />
                                    </ExtendedWarrantyContextProvider>
                                }
                            />
                        </Route>
                    ) : null}
                    {isCompanyEntityEnabled?.is_enabled ? (
                        <Route path="companies" element={<Outlet />}>
                            <Route index element={<CompanyOverview />} />
                            <Route
                                path="my-companies"
                                element={
                                    <PublicLayoutContent
                                        dottedBackgroundProps={{ gradient: true, gradientValue: '10%' }}
                                    >
                                        <CompaniesList />
                                    </PublicLayoutContent>
                                }
                            />
                            <Route path="new" element={<CompanySetup />} />
                        </Route>
                    ) : null}
                    <Route
                        path="rla-form"
                        element={
                            user &&
                            [
                                Roles.VICTRON,
                                Roles.SALES_DIRECTOR,
                                Roles.SALES_MANAGER,
                                Roles.DISTRIBUTOR,
                                Roles.DEALER,
                                Roles.INSTALLER,
                                Roles.AUTHORIZED_INSTALLER,
                            ].includes(user?.role) ? (
                                <RLAForm />
                            ) : (
                                <PublicLayoutOptions hideChrome>
                                    <SplitLayout>
                                        <Forbidden />
                                    </SplitLayout>
                                </PublicLayoutOptions>
                            )
                        }
                    />
                    <Route path="developers/*" element={<DevelopersRoutes />} />
                    <Route
                        path="media-assets/*"
                        element={
                            <PublicLayoutOptions extraToolbar={MEDIA_ASSETS_TOOLBAR}>
                                <MediaAssets />
                            </PublicLayoutOptions>
                        }
                    />
                    <Route path="repairs/*" element={<RepairDocs />} />
                    <Route path="online-training/*" element={<OnlineTrainingViews />} />
                    <Route path="accounts/*" element={<AccountRoutes />} />
                    <Route path="videos/*" element={<VideosRoutes />} />
                    <Route path="blog/*" element={<BlogViews />} />
                </Route>
                {isAuthInstEnabled?.is_enabled ? (
                    <Route element={<ProtectedRoutes countries={countryRestrictions} />}>
                        <Route path="authorized-installers" element={<Outlet />}>
                            <Route index element={<Navigate to="apply" replace />} />
                            <Route path="apply" element={<AuthorizedInstallerApplicationForm />} />
                        </Route>
                    </Route>
                ) : null}
                <Route path="events" element={<Outlet />}>
                    <Route element={<ProtectedRoutes />}>
                        <Route
                            index
                            element={
                                <PublicLayoutOptions dottedBackgroundProps={EVENT_LIST_DOTTED_BACKGROUND}>
                                    <EventList />
                                </PublicLayoutOptions>
                            }
                        />
                    </Route>
                    <Route path=":type/:eventId" element={<EventDetail />} />
                </Route>
                <Route path="news/*" element={<NewsRoutes />} />
                <Route path="software-integrators-program" element={<SoftwareIntegrator />} />
                <Route path="firmware/*" element={<Firmwares />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </PublicLayout>
    );
};

export { PublicViews };
